import { DateTime } from "luxon";
import { useMemo } from "react";
import type { Nurse, SpecificPreference } from "../../utils/models";
import { buildPtoByDayISO } from "./buildPtoByDayISO";

/**
 * `buildPtoByDayISO`, rebuilt only when the days' dates, the nurses, the day start or the timezone change.
 *
 * The days arrive as new objects on every schedule or requests change, and none of those moves anyone's time off, so
 * the map is keyed on their ISO dates instead. Time-off edits go through `updateNurse`, which replaces `nurses`.
 */
export const usePtoByDayISO = (
	days: DateTime[],
	nurses: Pick<Nurse, "employeeSpecificPreferences">[],
	dayStart: string,
	timezone: string,
): Map<string, SpecificPreference[]> => {
	const dayIsoKey = days
		.map((day) => day.toISODate())
		.filter((iso): iso is string => iso !== null)
		.join(",");
	return useMemo(() => {
		const isoDays = dayIsoKey === "" ? [] : dayIsoKey.split(",");
		return buildPtoByDayISO(
			isoDays.map((iso) => DateTime.fromISO(iso, { zone: timezone })),
			nurses,
			dayStart,
		);
	}, [dayIsoKey, nurses, dayStart, timezone]);
};
